/**
 * aeterna/neural/neat.ts — NEAT Topology Evolution
 *
 * NeuroEvolution of Augmenting Topologies. The Aeterna doesn't have a
 * fixed architecture — it grows its own brain over time.
 *
 * Starting from a minimal 3-node network, structural mutations add
 * neurons and connections in response to prediction error patterns.
 * After months of interaction, two Aeternas from the same seed will
 * have literally different neural architectures — not just different
 * weights, but different structures.
 *
 * The Aeterna's curiosity about music isn't a parameter. It's a
 * subgraph that grew because music kept appearing in its prediction
 * errors.
 *
 * Speciation protects innovation: new topologies get a grace period
 * before competing against established ones. This prevents premature
 * convergence and allows novel structures to develop.
 *
 * Designed to run in a background worker thread between sessions,
 * or incrementally during idle frames.
 */

import { mulberry32, gaussianSeeded, sigmoid, zeros } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Maximum neurons a genome can grow to */
const MAX_NODES = 64;

/** Maximum connections */
const MAX_CONNECTIONS = 256;

/** Mutation rates */
const MUTATE_WEIGHT_RATE     = 0.8;   // probability of weight perturbation
const MUTATE_WEIGHT_SIGMA    = 0.3;   // stddev of weight perturbation
const MUTATE_ADD_NODE_RATE   = 0.03;  // probability of adding a node
const MUTATE_ADD_CONN_RATE   = 0.05;  // probability of adding a connection
const MUTATE_TOGGLE_RATE     = 0.01;  // probability of toggling a connection
const MUTATE_WEIGHT_REPLACE  = 0.1;   // probability of replacing weight entirely

/** Compatibility distance coefficients */
const C1_EXCESS    = 1.0;
const C2_DISJOINT  = 1.0;
const C3_WEIGHT    = 0.4;

/** Species compatibility threshold */
const COMPAT_THRESHOLD = 3.0;

/** Staleness: generations without improvement before species is penalized */
const STALE_GENERATIONS = 15;

// ─── Innovation Counter ──────────────────────────────────────────────────────

let globalInnovation = 0;
function nextInnovation(): number { return globalInnovation++; }

// ─── Gene Types ──────────────────────────────────────────────────────────────

export type ActivationFn = "sigmoid" | "tanh" | "relu" | "identity" | "gaussian";

export interface NodeGene {
  id: number;
  type: "input" | "output" | "hidden";
  activation: ActivationFn;
  /** Bias for this node */
  bias: number;
  /** Layer hint for feed-forward ordering (input=0, output=1, hidden in between) */
  layer: number;
}

export interface ConnectionGene {
  innovation: number;
  from: number;
  to: number;
  weight: number;
  enabled: boolean;
}

// ─── Genome ──────────────────────────────────────────────────────────────────

export class NEATGenome {
  nodes: NodeGene[] = [];
  connections: ConnectionGene[] = [];
  fitness: number = 0;
  adjustedFitness: number = 0;
  speciesId: number = -1;

  private nextNodeId = 0;

  /** Create a minimal genome with given input/output counts */
  static createMinimal(inputCount: number, outputCount: number, seed = 42): NEATGenome {
    const g = new NEATGenome();
    const rng = mulberry32(seed);

    // Input nodes
    for (let i = 0; i < inputCount; i++) {
      g.nodes.push({ id: g.nextNodeId++, type: "input", activation: "identity", bias: 0, layer: 0 });
    }

    // Output nodes
    for (let i = 0; i < outputCount; i++) {
      g.nodes.push({ id: g.nextNodeId++, type: "output", activation: "tanh", bias: 0, layer: 1 });
    }

    // Connect all inputs to all outputs (fully connected minimal)
    for (let i = 0; i < inputCount; i++) {
      for (let o = 0; o < outputCount; o++) {
        g.connections.push({
          innovation: nextInnovation(),
          from: i,
          to: inputCount + o,
          weight: gaussianSeeded(rng) * 0.5,
          enabled: true,
        });
      }
    }

    return g;
  }

  /** Deep clone */
  clone(): NEATGenome {
    const g = new NEATGenome();
    g.nodes = this.nodes.map(n => ({ ...n }));
    g.connections = this.connections.map(c => ({ ...c }));
    g.nextNodeId = this.nextNodeId;
    g.fitness = this.fitness;
    return g;
  }

  // ─── Mutations ─────────────────────────────────────────────────────────

  /** Apply all mutation operators */
  mutate(rng: () => number): void {
    // Weight mutations
    for (const conn of this.connections) {
      if (rng() < MUTATE_WEIGHT_RATE) {
        if (rng() < MUTATE_WEIGHT_REPLACE) {
          conn.weight = gaussianSeeded(rng);
        } else {
          conn.weight += gaussianSeeded(rng) * MUTATE_WEIGHT_SIGMA;
        }
      }
    }

    // Bias mutations
    for (const node of this.nodes) {
      if (node.type !== "input" && rng() < 0.3) {
        node.bias += gaussianSeeded(rng) * 0.1;
      }
    }

    // Structural: add node
    if (rng() < MUTATE_ADD_NODE_RATE && this.nodes.length < MAX_NODES) {
      this.mutateAddNode(rng);
    }

    // Structural: add connection
    if (rng() < MUTATE_ADD_CONN_RATE && this.connections.length < MAX_CONNECTIONS) {
      this.mutateAddConnection(rng);
    }

    // Toggle connection
    if (rng() < MUTATE_TOGGLE_RATE && this.connections.length > 0) {
      const idx = Math.floor(rng() * this.connections.length);
      this.connections[idx].enabled = !this.connections[idx].enabled;
    }
  }

  /** Add a node by splitting an existing connection */
  private mutateAddNode(rng: () => number): void {
    const enabled = this.connections.filter(c => c.enabled);
    if (enabled.length === 0) return;

    const conn = enabled[Math.floor(rng() * enabled.length)];
    conn.enabled = false;

    const fromNode = this.nodes.find(n => n.id === conn.from);
    const toNode = this.nodes.find(n => n.id === conn.to);
    if (!fromNode || !toNode) return;

    const newNode: NodeGene = {
      id: this.nextNodeId++,
      type: "hidden",
      activation: rng() < 0.5 ? "tanh" : "sigmoid",
      bias: 0,
      layer: (fromNode.layer + toNode.layer) / 2,
    };
    this.nodes.push(newNode);

    // Connection from → new (weight 1.0 to preserve behavior)
    this.connections.push({
      innovation: nextInnovation(),
      from: conn.from,
      to: newNode.id,
      weight: 1.0,
      enabled: true,
    });

    // Connection new → to (original weight to preserve behavior)
    this.connections.push({
      innovation: nextInnovation(),
      from: newNode.id,
      to: conn.to,
      weight: conn.weight,
      enabled: true,
    });
  }

  /** Add a new connection between two unconnected nodes */
  private mutateAddConnection(rng: () => number): void {
    // Pick random from and to nodes (from must be earlier layer)
    const attempts = 20;
    for (let a = 0; a < attempts; a++) {
      const from = this.nodes[Math.floor(rng() * this.nodes.length)];
      const to = this.nodes[Math.floor(rng() * this.nodes.length)];

      if (from.id === to.id) continue;
      if (to.type === "input") continue;
      if (from.type === "output") continue;
      if (from.layer >= to.layer && from.type !== "input") continue;

      // Check if connection already exists
      const exists = this.connections.some(
        c => c.from === from.id && c.to === to.id
      );
      if (exists) continue;

      this.connections.push({
        innovation: nextInnovation(),
        from: from.id,
        to: to.id,
        weight: gaussianSeeded(rng),
        enabled: true,
      });
      return;
    }
  }

  // ─── Crossover ─────────────────────────────────────────────────────────

  /** Crossover two genomes (this = more fit parent) */
  static crossover(parent1: NEATGenome, parent2: NEATGenome, rng: () => number): NEATGenome {
    const child = new NEATGenome();

    // Use fitter parent's nodes
    child.nodes = parent1.nodes.map(n => ({ ...n }));
    child.nextNodeId = Math.max(parent1.nextNodeId, parent2.nextNodeId);

    // Align connections by innovation number
    const p2Map = new Map(parent2.connections.map(c => [c.innovation, c]));

    for (const c1 of parent1.connections) {
      const c2 = p2Map.get(c1.innovation);
      if (c2) {
        // Matching gene: randomly inherit from either parent
        child.connections.push(rng() < 0.5 ? { ...c1 } : { ...c2 });
      } else {
        // Excess/disjoint: inherit from fitter parent (parent1)
        child.connections.push({ ...c1 });
      }
    }

    return child;
  }

  // ─── Compatibility Distance ────────────────────────────────────────────

  static compatibilityDistance(g1: NEATGenome, g2: NEATGenome): number {
    const map1 = new Map(g1.connections.map(c => [c.innovation, c]));
    const map2 = new Map(g2.connections.map(c => [c.innovation, c]));

    let matching = 0;
    let disjoint = 0;
    let excess = 0;
    let weightDiff = 0;

    const maxInnov1 = g1.connections.reduce((m, c) => Math.max(m, c.innovation), 0);
    const maxInnov2 = g2.connections.reduce((m, c) => Math.max(m, c.innovation), 0);

    const allInnovs = new Set([...map1.keys(), ...map2.keys()]);
    for (const innov of allInnovs) {
      const in1 = map1.has(innov);
      const in2 = map2.has(innov);

      if (in1 && in2) {
        matching++;
        weightDiff += Math.abs(map1.get(innov)!.weight - map2.get(innov)!.weight);
      } else if (innov > maxInnov1 || innov > maxInnov2) {
        excess++;
      } else {
        disjoint++;
      }
    }

    const N = Math.max(g1.connections.length, g2.connections.length, 1);
    const avgWeight = matching > 0 ? weightDiff / matching : 0;

    return (C1_EXCESS * excess) / N + (C2_DISJOINT * disjoint) / N + C3_WEIGHT * avgWeight;
  }

  // ─── Feed Forward ──────────────────────────────────────────────────────

  /** Evaluate the network on an input vector */
  evaluate(input: Float32Array): Float32Array {
    const values = new Map<number, number>();

    // Set input values
    const inputNodes = this.nodes.filter(n => n.type === "input");
    for (let i = 0; i < inputNodes.length; i++) {
      values.set(inputNodes[i].id, input[i] ?? 0);
    }

    // Topological order: sort by layer
    const sortedNodes = [...this.nodes]
      .filter(n => n.type !== "input")
      .sort((a, b) => a.layer - b.layer);

    // Process each non-input node
    for (const node of sortedNodes) {
      let sum = node.bias;
      for (const conn of this.connections) {
        if (!conn.enabled || conn.to !== node.id) continue;
        const fromVal = values.get(conn.from) ?? 0;
        sum += fromVal * conn.weight;
      }
      values.set(node.id, activate(sum, node.activation));
    }

    // Read output values
    const outputNodes = this.nodes.filter(n => n.type === "output");
    const output = new Float32Array(outputNodes.length);
    for (let i = 0; i < outputNodes.length; i++) {
      output[i] = values.get(outputNodes[i].id) ?? 0;
    }
    return output;
  }

  /** Count of hidden nodes (complexity measure) */
  get complexity(): number {
    return this.nodes.filter(n => n.type === "hidden").length;
  }

  /** Count of enabled connections */
  get activeConnections(): number {
    return this.connections.filter(c => c.enabled).length;
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      nodes: this.nodes,
      connections: this.connections,
      nextNodeId: this.nextNodeId,
      fitness: this.fitness,
      globalInnovation: globalInnovation,
    });
  }

  static deserialize(json: string): NEATGenome {
    const data = JSON.parse(json);
    const g = new NEATGenome();
    g.nodes = data.nodes;
    g.connections = data.connections;
    g.nextNodeId = data.nextNodeId;
    g.fitness = data.fitness;
    // Restore global innovation counter — use explicit value if present,
    // otherwise fall back to scanning connections (legacy data)
    if (data.globalInnovation != null && data.globalInnovation > globalInnovation) {
      globalInnovation = data.globalInnovation;
    } else {
      for (const c of g.connections) {
        if (c.innovation >= globalInnovation) globalInnovation = c.innovation + 1;
      }
    }
    return g;
  }
}

// ─── Activation Functions ────────────────────────────────────────────────────

function activate(x: number, fn: ActivationFn): number {
  switch (fn) {
    case "sigmoid":  return sigmoid(x);
    case "tanh":     return Math.tanh(x);
    case "relu":     return x > 0 ? x : 0;
    case "identity": return x;
    case "gaussian": return Math.exp(-x * x);
  }
}

// ─── Species ─────────────────────────────────────────────────────────────────

interface Species {
  id: number;
  representative: NEATGenome;
  members: NEATGenome[];
  bestFitness: number;
  staleGenerations: number;
}

// ─── Population ──────────────────────────────────────────────────────────────

export class NEATPopulation {
  private species: Species[] = [];
  private generation = 0;
  private nextSpeciesId = 0;
  private bestGenome: NEATGenome | null = null;
  private rng: () => number;

  readonly populationSize: number;
  readonly inputDim: number;
  readonly outputDim: number;

  constructor(populationSize: number, inputDim: number, outputDim: number, seed = 42) {
    this.populationSize = populationSize;
    this.inputDim = inputDim;
    this.outputDim = outputDim;
    this.rng = mulberry32(seed);
  }

  /** Initialize the population with minimal genomes */
  initialize(): void {
    const genomes: NEATGenome[] = [];
    for (let i = 0; i < this.populationSize; i++) {
      const g = NEATGenome.createMinimal(this.inputDim, this.outputDim, i * 1000);
      g.mutate(this.rng);
      genomes.push(g);
    }
    this.speciate(genomes);
  }

  /**
   * Evolve one generation.
   * fitnessFunc evaluates a genome → fitness score.
   * Returns the best genome of this generation.
   */
  evolve(fitnessFunc: (genome: NEATGenome) => number): NEATGenome {
    // Evaluate fitness
    for (const sp of this.species) {
      for (const member of sp.members) {
        member.fitness = fitnessFunc(member);
        if (!this.bestGenome || member.fitness > this.bestGenome.fitness) {
          this.bestGenome = member.clone();
        }
      }
    }

    // Adjusted fitness (share within species)
    for (const sp of this.species) {
      for (const member of sp.members) {
        member.adjustedFitness = member.fitness / Math.max(1, sp.members.length);
      }

      // Track staleness
      const bestInSpecies = Math.max(...sp.members.map(m => m.fitness));
      if (bestInSpecies > sp.bestFitness) {
        sp.bestFitness = bestInSpecies;
        sp.staleGenerations = 0;
      } else {
        sp.staleGenerations++;
      }
    }

    // Produce offspring
    const totalAdjusted = this.species.reduce(
      (sum, sp) => sum + sp.members.reduce((s, m) => s + m.adjustedFitness, 0), 0
    );

    const offspring: NEATGenome[] = [];

    for (const sp of this.species) {
      if (sp.staleGenerations > STALE_GENERATIONS) continue; // kill stale species

      const spAdjusted = sp.members.reduce((s, m) => s + m.adjustedFitness, 0);
      let count = Math.max(1, Math.round(
        (spAdjusted / (totalAdjusted + 1e-10)) * this.populationSize
      ));

      // Sort by fitness
      sp.members.sort((a, b) => b.fitness - a.fitness);

      // Elitism: keep best
      if (sp.members.length > 0) {
        offspring.push(sp.members[0].clone());
        count--;
      }

      // Produce rest via crossover + mutation
      const pool = sp.members.slice(0, Math.max(1, Math.ceil(sp.members.length * 0.5)));
      for (let i = 0; i < count; i++) {
        const p1 = pool[Math.floor(this.rng() * pool.length)];
        const p2 = pool[Math.floor(this.rng() * pool.length)];
        const child = p1.fitness >= p2.fitness
          ? NEATGenome.crossover(p1, p2, this.rng)
          : NEATGenome.crossover(p2, p1, this.rng);
        child.mutate(this.rng);
        offspring.push(child);
      }
    }

    // Fill remaining slots if species died
    while (offspring.length < this.populationSize) {
      const g = NEATGenome.createMinimal(this.inputDim, this.outputDim, this.generation * 10000 + offspring.length);
      g.mutate(this.rng);
      offspring.push(g);
    }

    this.speciate(offspring);
    this.generation++;

    return this.bestGenome ?? offspring[0];
  }

  /** Assign genomes to species */
  private speciate(genomes: NEATGenome[]): void {
    // Clear members
    for (const sp of this.species) sp.members = [];

    for (const genome of genomes) {
      let placed = false;
      for (const sp of this.species) {
        if (NEATGenome.compatibilityDistance(genome, sp.representative) < COMPAT_THRESHOLD) {
          sp.members.push(genome);
          genome.speciesId = sp.id;
          placed = true;
          break;
        }
      }

      if (!placed) {
        const sp: Species = {
          id: this.nextSpeciesId++,
          representative: genome.clone(),
          members: [genome],
          bestFitness: 0,
          staleGenerations: 0,
        };
        genome.speciesId = sp.id;
        this.species.push(sp);
      }
    }

    // Remove empty species, update representatives
    this.species = this.species.filter(sp => sp.members.length > 0);
    for (const sp of this.species) {
      sp.representative = sp.members[Math.floor(this.rng() * sp.members.length)].clone();
    }
  }

  /** Get the best genome found so far */
  getBest(): NEATGenome | null { return this.bestGenome; }

  /** Current generation */
  getGeneration(): number { return this.generation; }

  /** Number of active species */
  getSpeciesCount(): number { return this.species.length; }

  /** Serialize the best genome + population metadata for persistence */
  serializeBest(): string | null {
    if (!this.bestGenome) return null;
    return JSON.stringify({
      genome: this.bestGenome.serialize(),
      generation: this.generation,
      speciesCount: this.species.length,
      globalInnovation: globalInnovation,
    });
  }

  /** Restore from serialized best genome + metadata */
  static restoreBest(json: string): { genome: NEATGenome; generation: number } {
    const data = JSON.parse(json);
    // Restore global innovation counter FIRST (before deserializing genome)
    if (data.globalInnovation != null && data.globalInnovation > globalInnovation) {
      globalInnovation = data.globalInnovation;
    }
    const genome = NEATGenome.deserialize(data.genome ?? json);
    return { genome, generation: data.generation ?? 0 };
  }
}
